<mj-page-layout>
    <mj-page-body>
        <!--
          The mockup's `.top`: a bare 27px/800 h1 over one sub line, no icon
          chip and no header badge — the 30-day deadline count is the `.modenote`
          sentence below, not a pill in the header (saved.html:341-343).
        -->
        <agu-plain-header Title="Saved" [Sub]="Subtitle" />
        <p class="ra-saved__note">{{ ModeNote }}</p>

        <div class="ra-saved__filterbar">
            <agu-tab-strip Variant="pill" [PhoneSegmented]="true" [Tabs]="Tabs" [Active]="activeTab" [PanelId]="ResultsPanelId" (ActiveChange)="OnTabChange($event)" />

            <div class="ra-saved__controls">
                <label class="ra-saved__search">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg>
                    <input type="text" placeholder="Search…" aria-label="Search saved items" [value]="searchQuery" (input)="OnSearchInput($event)" />
                </label>

                <!--
                  The mockup's `#sort` is a plain native select whose closed state reads the option
                  alone — `Recently saved`, with no glyph and no label prefix (saved.html:339, filled from
                  SORTS at :377). The browser paints the option list; the chevron is ours (:222-230).
                -->
                <span class="ra-saved__sort">
                    <select #sortSelect aria-label="Sort saved items" (change)="OnSortChange(sortSelect.value)">
                        @for (option of SortOptions; track option.Key) {
                            <option [value]="option.Key" [selected]="option.Key === sort">{{ option.Label }}</option>
                        }
                    </select>
                </span>
            </div>
        </div>

        <div class="ra-saved__results" [id]="ResultsPanelId" role="tabpanel">
            <!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), the error panel (P/proto-states.js:115), nothing saved (:104). -->
            @if (loading) {
                <agu-skeleton [Count]="3" />
            } @else if (error) {
                <agu-state-panel
                    Variant="error"
                    [Icon]="ErrorCopy.Icon"
                    [Title]="ErrorCopy.Title"
                    [Body]="ErrorCopy.Body"
                    PrimaryLabel="Try again"
                    [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
                    [SecondaryHref]="failures.SupportHref"
                    [Foot]="failures.Foot"
                    [Note]="failures.Note"
                    (Primary)="Retry()"
                />
            } @else {
                <!--
                  Empty state and Undo strip are grid children, exactly as they
                  are in the mockup (`.empty` and `.undo` both span
                  `grid-column: 1 / -1`, saved.html:294-300) — so removing the
                  last saved card leaves the Undo reachable under the empty
                  state instead of vanishing with the cards.
                -->
                <div class="ra-saved__grid">
                    @if (IsEmpty) {
                        <div class="ra-saved__span">
                            @if (!HasAnySaved) {
                                <agu-state-panel
                                    [Icon]="EmptyCopy.Icon"
                                    [Title]="EmptyCopy.Title"
                                    [Body]="EmptyCopy.Body"
                                    PrimaryLabel="Browse grants"
                                    SecondaryLabel="Browse jobs"
                                    (Primary)="OnBrowse('grants')"
                                    (Secondary)="OnBrowse('jobs')"
                                />
                            } @else {
                                <agu-state-panel Icon="search" Title="Nothing matches your filters." PrimaryLabel="Clear filters" (Primary)="OnClear()" />
                            }
                        </div>
                    } @else {
                        <!--
                          Each cell carries its OWN kind's accent, which is what
                          the mockup's per-card `--c` does (saved.html:382) —
                          one board, three colours, so a job card reads orange
                          beside a teal grant. The three finder cards are reused
                          as-is; the only board-specific addition is the grant's
                          deadline chip, which rides in the card's own chip row
                          rather than being drawn over the cell.
                        -->
                        @for (row of FilteredRows; track row.key) {
                            <!-- Card titles are h2 on the Saved board (P/for-you.html:947). -->
                            <div class="ra-saved__cell" [attr.data-accent]="AccentFor(row.kind)">
                                @if (IsGrantRow(row)) {
                                    <ra-grant-card
                                        [Grant]="row.grant"
                                        [DueChip]="row.dueChip"
                                        [ShowPin]="false"
                                        [ShowDismiss]="false"
                                        [TitleLevel]="2"
                                        (Save)="OnCardSave(row, $event)"
                                        (Open)="OnOpenRow(row)"
                                    />
                                } @else if (IsPersonRow(row)) {
                                    <ra-person-card
                                        [Person]="row.person"
                                        [ShowPin]="false"
                                        [ShowDismiss]="false"
                                        [TitleLevel]="2"
                                        (Save)="OnCardSave(row, $event)"
                                        (Open)="OnOpenRow(row)"
                                    />
                                } @else if (IsJobRow(row)) {
                                    <ra-job-card [Job]="row.job" [ShowPin]="false" [ShowDismiss]="false" [TitleLevel]="2" (Save)="OnCardSave(row, $event)" (Open)="OnOpenRow(row)" />
                                }
                            </div>
                        }
                    }

                    @if (lastRemoved) {
                        <p class="ra-saved__undo" role="status">
                            <span>Removed from saved: <b>{{ lastRemoved.title }}</b>.</span>
                            <button type="button" class="ra-saved__undo-btn agu-hit-24" (click)="Undo()">Undo</button>
                        </p>
                    }
                </div>
            }
        </div>
    </mj-page-body>
</mj-page-layout>
